/** Colour helpers: keep coloured text (tags, labels) readable on its tinted background. */

export type RGB = readonly [number, number, number];

/** `#rgb` or `#rrggbb` → RGB, or null for anything else. */
export function parseHexColor(color: string): RGB | null {
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(color.trim());
  if (!m) return null;
  const h = m[1]!.length === 3 ? [...m[1]!].map((c) => c + c).join('') : m[1]!;
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16)) as unknown as RGB;
}

export function toHexColor(c: RGB): string {
  return `#${c
    .map((v) =>
      Math.round(Math.max(0, Math.min(255, v)))
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')}`;
}

/** Same as CSS `color-mix(in srgb, a p, b)`, with `p` from 0 to 1. */
export function mixColors(a: RGB, b: RGB, p: number): RGB {
  return [0, 1, 2].map((i) => a[i]! * p + b[i]! * (1 - p)) as unknown as RGB;
}

function luminance(c: RGB): number {
  const [r, g, b] = c.map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio, 1 to 21. */
export function contrastRatio(a: RGB, b: RGB): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (hi + 0.05) / (lo + 0.05);
}

/** The opacity of a tag's tinted background over the page. */
export const TAG_TINT = 0.13;

/** Each theme's ink and the surfaces a chip can sit on (page, hover, selected, raised). */
export const CHIP_THEMES = {
  light: {
    ink: [27, 37, 38],
    surfaces: [
      [255, 255, 255],
      [238, 242, 242],
      [227, 235, 234],
      [227, 243, 241],
    ],
  },
  dark: {
    ink: [230, 238, 238],
    surfaces: [
      [15, 21, 22],
      [27, 37, 38],
      [35, 48, 49],
      [18, 48, 46],
    ],
  },
} as const satisfies Record<string, { ink: RGB; surfaces: readonly RGB[] }>;

/**
 * Text colour for a chip tinted with `color`: the colour itself when it is readable on every
 * surface, otherwise mixed toward the theme's ink until it reaches `min` contrast (WCAG AA for
 * small text).
 */
export function readableChipText(color: string, theme: 'light' | 'dark', min = 4.5): string {
  const c = parseHexColor(color);
  if (!c) return color;
  const { ink, surfaces } = CHIP_THEMES[theme];
  const tints = surfaces.map((s) => mixColors(c, s, TAG_TINT));
  for (let p = 1; p > 0; p -= 0.05) {
    const fg = mixColors(c, ink, p);
    if (tints.every((t) => contrastRatio(fg, t) >= min)) return toHexColor(fg);
  }
  return toHexColor(ink);
}

/**
 * Background for a solid pill with white text (status pills): the colour itself when white is
 * readable on it, otherwise darkened just enough.
 */
export function readablePillBackground(color: string, min = 4.5): string {
  const c = parseHexColor(color);
  if (!c) return color;
  const white: RGB = [255, 255, 255];
  const black: RGB = [0, 0, 0];
  for (let p = 1; p > 0; p -= 0.04) {
    const bg = mixColors(c, black, p);
    if (contrastRatio(white, bg) >= min) return toHexColor(bg);
  }
  return '#000000';
}
